import React from 'react';
import { Link } from 'react-router-dom';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import SEO, { createBreadcrumbSchema, createFAQSchema, softwareApplicationSchema } from '@/components/SEO';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ArrowRight, CheckCircle, X, Zap, Clock, DollarSign, Image, Palette, Target } from 'lucide-react';

const FluxVsStableDiffusion = () => {
  const comparisonData = [
    { feature: 'Image Quality', flux: '⭐⭐⭐⭐⭐', sd: '⭐⭐⭐⭐' },
    { feature: 'Text Rendering', flux: '⭐⭐⭐⭐⭐', sd: '⭐⭐⭐' },
    { feature: 'Prompt Understanding', flux: '⭐⭐⭐⭐⭐', sd: '⭐⭐⭐⭐' },
    { feature: 'Photorealism', flux: '⭐⭐⭐⭐⭐', sd: '⭐⭐⭐⭐' },
    { feature: 'Speed', flux: '⭐⭐⭐⭐', sd: '⭐⭐⭐⭐⭐' },
    { feature: 'Customization', flux: '⭐⭐⭐⭐', sd: '⭐⭐⭐⭐⭐' },
    { feature: 'Cost per Image', flux: '$$', sd: '$' },
    { feature: 'Ease of Use', flux: '⭐⭐⭐⭐⭐', sd: '⭐⭐⭐' },
  ];

  const faqs = [
    {
      question: "Is FLUX better than Stable Diffusion for product photos?",
      answer: "FLUX excels at photorealistic product images with better text rendering and consistent lighting. Stable Diffusion offers more customization but requires more prompt engineering for commercial results."
    },
    {
      question: "Which is faster, FLUX or Stable Diffusion?",
      answer: "Stable Diffusion can be faster when self-hosted with optimized hardware. FLUX through our API generates high-quality images in 3-5 seconds, optimized for consistency."
    },
    {
      question: "Can I run FLUX locally like Stable Diffusion?",
      answer: "FLUX models are available but require significant GPU power. Our cloud platform removes hardware requirements, letting you access FLUX quality without expensive setup."
    },
    {
      question: "Which AI is best for commercial use?",
      answer: "FLUX has clearer commercial licensing through official APIs. Stable Diffusion offers flexibility but requires careful attention to model-specific licenses and training data sources."
    }
  ];

  const fluxAdvantages = [
    "Superior text rendering in images",
    "Better prompt understanding out of the box",
    "Consistent commercial-quality output",
    "No setup or hardware required",
    "Clear commercial licensing"
  ];

  const sdAdvantages = [
    "Open source and highly customizable",
    "Can run locally on your hardware",
    "Large community of fine-tuned models",
    "Lower cost when self-hosted",
    "ControlNet and other extensions"
  ];

  return (
    <>
      <SEO
        title="FLUX vs Stable Diffusion 2026 | Complete AI Image Generator Comparison"
        description="Comprehensive comparison of FLUX and Stable Diffusion. Which AI image generator is better for your needs? Quality, speed, pricing, and use cases compared."
        keywords="flux vs stable diffusion, ai image generator comparison, flux 1.1 pro vs sd, stable diffusion alternative, best ai image generator 2026, flux ai review, stable diffusion review"
        url="/flux-vs-stable-diffusion"
        type="article"
        jsonLd={[
          softwareApplicationSchema,
          createBreadcrumbSchema([
            { name: 'Home', url: '/' },
            { name: 'FLUX vs Stable Diffusion', url: '/flux-vs-stable-diffusion' }
          ]),
          createFAQSchema(faqs)
        ]}
      />
      
      <Header />
      
      <main className="min-h-screen bg-background">
        {/* Hero Section */}
        <section className="relative py-20 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-purple-500/10 via-blue-500/5 to-background" />
          <div className="container mx-auto px-4 relative z-10">
            <div className="max-w-4xl mx-auto text-center">
              <Badge className="mb-4 bg-purple-500/10 text-purple-500 border-purple-500/20">
                2026 Comparison Guide
              </Badge>
              <h1 className="text-4xl md:text-6xl font-bold mb-6">
                FLUX vs Stable Diffusion
                <span className="block text-purple-500">Complete 2026 Comparison</span>
              </h1>
              <p className="text-xl text-muted-foreground mb-8 max-w-2xl mx-auto">
                Comprehensive comparison of the two leading AI image generators. 
                Quality, speed, pricing, and real-world performance analyzed.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button asChild size="lg" className="bg-purple-500 hover:bg-purple-600">
                  <Link to="/try-generator">
                    Try FLUX Free <ArrowRight className="ml-2 h-5 w-5" />
                  </Link>
                </Button>
              </div>
            </div>
          </div>
        </section>

        {/* Comparison Table */}
        <section className="py-16 bg-muted/30">
          <div className="container mx-auto px-4">
            <h2 className="text-3xl font-bold text-center mb-12">
              Feature-by-Feature Comparison
            </h2>
            <div className="max-w-4xl mx-auto">
              <Card>
                <CardContent className="p-0">
                  <div className="grid grid-cols-3 gap-4 p-4 bg-muted/50 font-semibold border-b">
                    <div>Feature</div>
                    <div className="text-center">FLUX</div>
                    <div className="text-center">Stable Diffusion</div>
                  </div>
                  {comparisonData.map((row, index) => (
                    <div key={index} className="grid grid-cols-3 gap-4 p-4 border-b last:border-b-0">
                      <div>{row.feature}</div>
                      <div className="text-center">{row.flux}</div>
                      <div className="text-center">{row.sd}</div>
                    </div>
                  ))}
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* Pros Comparison */}
        <section className="py-16">
          <div className="container mx-auto px-4">
            <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
              {/* FLUX Advantages */}
              <Card className="border-purple-500/20">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-purple-500">
                    <Zap className="w-5 h-5" />
                    FLUX Advantages
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  {fluxAdvantages.map((advantage, index) => (
                    <div key={index} className="flex items-center gap-3">
                      <CheckCircle className="w-5 h-5 text-purple-500 flex-shrink-0" />
                      <span>{advantage}</span>
                    </div>
                  ))}
                </CardContent>
              </Card>

              {/* SD Advantages */}
              <Card className="border-blue-500/20">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-blue-500">
                    <Palette className="w-5 h-5" />
                    Stable Diffusion Advantages
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  {sdAdvantages.map((advantage, index) => (
                    <div key={index} className="flex items-center gap-3">
                      <CheckCircle className="w-5 h-5 text-blue-500 flex-shrink-0" />
                      <span>{advantage}</span>
                    </div>
                  ))}
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* Use Case Recommendations */}
        <section className="py-16 bg-muted/30">
          <div className="container mx-auto px-4">
            <h2 className="text-3xl font-bold text-center mb-12">
              Which Should You Choose?
            </h2>
            <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
              <Card>
                <CardHeader>
                  <CardTitle>Choose FLUX If You Need...</CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  <p>• Commercial product photography</p>
                  <p>• Text or logos in images</p>
                  <p>• Consistent, predictable results</p>
                  <p>• No technical setup</p>
                  <p>• Clear commercial licensing</p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Choose Stable Diffusion If You Need...</CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  <p>• Maximum customization control</p>
                  <p>• Local/offline generation</p>
                  <p>• Specific fine-tuned models</p>
                  <p>• ControlNet/advanced features</p>
                  <p>• Lowest possible cost at scale</p>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-16">
          <div className="container mx-auto px-4">
            <h2 className="text-3xl font-bold text-center mb-12">
              Frequently Asked Questions
            </h2>
            <div className="max-w-3xl mx-auto space-y-6">
              {faqs.map((faq, index) => (
                <Card key={index}>
                  <CardContent className="p-6">
                    <h3 className="font-semibold mb-2">{faq.question}</h3>
                    <p className="text-muted-foreground">{faq.answer}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 bg-muted/30">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto text-center">
              <h2 className="text-3xl font-bold mb-6">
                Experience FLUX Quality for Free
              </h2>
              <p className="text-xl text-muted-foreground mb-8">
                See the FLUX difference yourself. Generate your first images free, 
                no credit card required.
              </p>
              <Button asChild size="lg" className="bg-purple-500 hover:bg-purple-600">
                <Link to="/try-generator">
                  Try FLUX Free Now <ArrowRight className="ml-2 h-5 w-5" />
                </Link>
              </Button>
            </div>
          </div>
        </section>
      </main>
      
      <Footer />
    </>
  );
};

export default FluxVsStableDiffusion;
